/**
 * GRIPS-DRAG-MORE-TYPES — ELLIPSE grip-drag → real-property translation.
 *
 * Split out of `gripDragTranslate.ts` (file-size discipline, CLAUDE.md
 * §"Контроль размера файлов" — keeps the base switch file's growth under the
 * 30% threshold). Same contract, same conventions as every other case in that
 * file's switch: given the entity's CURRENT properties + the LIVE document
 * entity (when threaded) + the dragged world point, return the real
 * geometry-property patch, or `null` for an unrecognised/degenerate grip.
 *
 * AutoCAD ELLIPSE shows 5 grips: center + 2 major-axis endpoints + 2
 * minor-axis endpoints (cad-core/grips.ts `ellipseGrips`, ported field-for-
 * field from the legacy `gripCalculation.ts` fallback):
 *   - "center" — pure translate (Center X/Y); the major-axis VECTOR and
 *     Ratio stay untouched (the whole ellipse moves as a rigid body).
 *   - "major-pos"/"major-neg" — rewrite the major-axis VECTOR (DXF group
 *     11/21, relative to center) — direction AND length — AND recompute
 *     "Ratio" so the ABSOLUTE minor-axis length is PRESERVED (local Codex
 *     review, GRIPS-DRAG-MORE-TYPES round 5): dragging one axis-endpoint
 *     grip elongates THAT axis only (Autodesk "About Editing with Grips" /
 *     general grip philosophy — each axis-endpoint grip is an independent
 *     one-dimensional stretch handle, mirroring how the minor-axis grip
 *     below already changes ONLY Ratio, never the major length). Leaving
 *     Ratio untouched here (the pre-round-5 behaviour) would silently scale
 *     the minor axis PROPORTIONALLY with the major-axis drag — a uniform
 *     resize a single-axis grip must never perform. The vector is stored as
 *     ONE endpoint's offset from center (no independent field for the
 *     opposite endpoint), so dragging "major-neg" negates the raw delta
 *     before writing it (mirrors the exact endpoint convention
 *     `ellipseGrips`/`gripCalculation.ts` render major-pos/major-neg with).
 *     If preserving the old minor length would require Ratio > 1 (the drag
 *     shrank the major axis shorter than the still-fixed minor axis), this
 *     clamps at Ratio=1 (a circle) — the same natural round-limit the
 *     minor-axis case below already uses, rather than re-parametrizing which
 *     axis is "major" (a DXF-level axis-swap AutoCAD does NOT expose through
 *     a plain grip drag).
 *   - "minor-pos"/"minor-neg" — change ONLY "Ratio" (DXF group 40, minor/
 *     major, constrained to (0, 1] by the typed writer's `exclusiveMin: 0,
 *     max: 1`). The minor-axis DIRECTION is always perpendicular to the
 *     major axis (not independently editable) — AutoCAD constrains this
 *     grip's rubber-band drag to the minor-axis LINE through the center, so
 *     only the SIGNED PROJECTION of the drag onto that line changes the
 *     ratio; dragging PAST the major-axis length clamps at Ratio=1 (a
 *     circle) rather than persisting an invalid ratio > 1. The ratio
 *     DENOMINATOR is the FULL 3-D major-axis length (round 7, local Codex
 *     review — DXF group 40 is minor/major of the true 3-D vector, so an
 *     imported TILTED ellipse's non-zero `majorAxisEndpoint.z` must count),
 *     while the drag-projection DIRECTION stays 2-D-only (a Z-inclusive
 *     normalizer would corrupt the 2-D projection itself, not just its
 *     units). The grip RENDER position (`packages/cad-core/src/grips.ts`
 *     `ellipseGrips` + the legacy `gripCalculation.ts` fallback) was
 *     updated to the SAME `majorMag3D * ratio` distance (round 8), so
 *     dragging exactly onto the visible handle of a tilted ellipse is a
 *     true no-op, not a silent shrink/grow.
 *
 * A TILTED ellipse (SL-15, `majorAxisEndpoint.z` "tilts the plane") is a
 * shared concern for the major- AND minor-axis branches: a grip drag never
 * writes "Major axis Z" (2-D mouse interaction), so the existing Z is
 * preserved by omission and every Ratio-affecting magnitude below is
 * computed Z-inclusive to match (rounds 5 + 6 + 7 + 8).
 */

import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

import { computeEllipseMinorGripRatio } from "./ellipseMinorGripRatio";
import { readRawCoord } from "./geometryEditRawPoint";

export { ellipseCoordinateUlp } from "./ellipseMinorGripRatio";

function fmt(n: number): string {
  return String(n);
}

export function translateEllipseGripDrag(
  p: Record<string, string>,
  documentEntity: DrawingEntity | undefined,
  gripId: string,
  wx: number,
  wy: number,
): Record<string, string> | null {
  // GEOMETRY-EDIT-INVARIANTS — source center + majorAxisEndpoint RAW from the
  // live document entity (full precision); the preview "Center X"/"Major
  // axis X"/… strings are formatNumber-rounded.
  const docEllipse =
    documentEntity?.type === "Ellipse" ? documentEntity : undefined;
  const cx = readRawCoord(docEllipse?.center.x, p["Center X"]);
  const cy = readRawCoord(docEllipse?.center.y, p["Center Y"]);
  if (gripId === "center") {
    return { "Center X": fmt(wx), "Center Y": fmt(wy) };
  }
  const majorX = readRawCoord(
    docEllipse?.majorAxisEndpoint.x,
    p["Major axis X"],
  );
  const majorY = readRawCoord(
    docEllipse?.majorAxisEndpoint.y,
    p["Major axis Y"],
  );
  // A TILTED ellipse (imported / non-planar, SL-15) carries a non-zero
  // group-31 Z on `majorAxisEndpoint` ("z tilts the plane",
  // `packages/drawing-model/src/model.ts`). A grip drag is a 2-D (X/Y-only)
  // mouse interaction and never touches this Z (only "Major axis X"/"Major
  // axis Y" are ever emitted below), so it stays exactly what it was. The
  // DXF ratio (group 40) is minor/major of the FULL 3-D vector length —
  // local Codex review (rounds 6 + 7) caught BOTH the major-axis Ratio
  // re-derivation and the minor-axis Ratio computation using the XY-only
  // projected length instead, understating the true major length on a
  // tilted ellipse and corrupting the preserved minor length. Every
  // Ratio-affecting magnitude below MUST include this same preserved Z.
  // Round 10 (local Codex review) — when no typed document entity is
  // threaded (the legacy/shell-only fallback path — the SAME two-tier
  // convention `cx`/`majorX`/`majorY` above already use), fall back to the
  // preview bag's "Major axis Z" string instead of silently defaulting to
  // 0: the fallback grip RENDERER (`gripCalculation.ts`, round 8) reads
  // THAT same preview field for a tilted ellipse, so defaulting to 0 here
  // would desync a shell-only drag from what the user visibly sees,
  // reintroducing the exact no-op-drag-mutates-Ratio bug rounds 6-8 closed
  // for the typed-document path.
  const majorZ = readRawCoord(
    docEllipse?.majorAxisEndpoint.z,
    p["Major axis Z"],
  );
  if (gripId === "major-pos" || gripId === "major-neg") {
    const dx = wx - cx;
    const dy = wy - cy;
    const vx = gripId === "major-pos" ? dx : -dx;
    const vy = gripId === "major-pos" ? dy : -dy;
    const newMajorMag = Math.hypot(vx, vy, majorZ);
    // A zero-length major axis is a degenerate (collapsed) ellipse — reject
    // rather than persist invalid DXF geometry (mirrors the Circle/Arc
    // "radius <= 0" reject). Z-inclusive: a drag onto the center's XY
    // projection is NOT degenerate when a preserved tilt-Z still gives the
    // vector a real (out-of-plane) length.
    if (newMajorMag <= 0) return null;
    // Preserve the ABSOLUTE minor-axis length (round 5 fix) — the OLD
    // ratio × OLD (3-D) major length is the minor length the drag must not
    // disturb; re-derive the ratio the NEW (3-D) major length would need to
    // keep it. `oldMajorMag <= 0` is a pre-existing degenerate document
    // (should not occur for a valid ellipse) — fall back to leaving Ratio
    // untouched rather than dividing by zero.
    const oldMajorMag = Math.hypot(majorX, majorY, majorZ);
    const patch: Record<string, string> = {
      "Major axis X": fmt(vx),
      "Major axis Y": fmt(vy),
    };
    if (oldMajorMag > 0) {
      const oldRatio = readRawCoord(docEllipse?.ratio, p["Ratio"], 1);
      const oldMinorMag = oldRatio * oldMajorMag;
      const newRatio = Math.min(oldMinorMag / newMajorMag, 1);
      if (newRatio > 0) patch.Ratio = fmt(newRatio);
    }
    return patch;
  }
  if (gripId === "minor-pos" || gripId === "minor-neg") {
    const ratio = computeEllipseMinorGripRatio({
      centerX: cx,
      centerY: cy,
      majorX,
      majorY,
      majorZ,
      orientation: gripId === "minor-pos" ? 1 : -1,
      worldX: wx,
      worldY: wy,
    });
    if (ratio === null) return null;
    return { Ratio: fmt(ratio) };
  }
  return null;
}
